import React, { useEffect, useMemo, useRef } from "react"
import Select, {
  components,
  MenuListProps,
  OptionProps,
} from "react-select"
import { SelectOption } from "modules/VesselSearch/VesselSearchMain"
import { FixedSizeList as List } from "react-window"
import type { VesselListSingle } from "modules/Prevetting/hooks/usePrevetGetVessels"
import { VesselSegment } from "user/userService"

export interface VesselTrackingVesselSearchProps {
  activeImos: string[]
  selectedImos: string[]
  setSelectedImos: (imos: string[]) => void
  vesselsData: VesselListSingle[] | undefined
  getVesselsLoading: boolean
  segmentFilter: string[] | null
  dwtMin: number | null
  dwtMax: number | null
  builtMin: number | null
  builtMax: number | null
  designFilter: string | null
  isMultiFilter: boolean
}

const ROW_HEIGHT = 35
const LIST_HEIGHT = 280
const HEADER_HEIGHT = 44

const CheckboxOption = (props: OptionProps<SelectOption, true>) => {
  return (
    <components.Option {...props}>
      <div className="d-flex align-items-center">
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => {}}
          className="me-2"
          style={{ pointerEvents: "none" }}
        />
        <span>{props.label}</span>
      </div>
    </components.Option>
  )
}

const VirtualizedMenuList = (props: MenuListProps<SelectOption, true>) => {
  const { options, children, getValue, selectProps } = props
  const value = getValue()
  const currentIndex = options.findIndex(
    (option) => "value" in option && option.value === value?.[0]?.value
  )

  const onSelectAll = (selectProps as any).onSelectAll as () => void
  const onClearAll = (selectProps as any).onClearAll as () => void
  const visibleCount = Array.isArray(children) ? children.length : 0

  const renderRow = ({
    index,
    style,
  }: {
    index: number
    style: React.CSSProperties
  }) => (
    <div style={style}>
      {children && (children as React.ReactNode[])[index]}
    </div>
  )

  return (
    <div>
      <div
        className="d-flex gap-2 align-items-center px-2 border-bottom"
        style={{
          height: HEADER_HEIGHT,
          background: "white",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onSelectAll?.()
          }}
        >
          Select All ({visibleCount})
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClearAll?.()
          }}
        >
          Clear All
        </button>
      </div>
      <List
        height={LIST_HEIGHT}
        itemCount={visibleCount}
        itemSize={ROW_HEIGHT}
        initialScrollOffset={Math.max(0, currentIndex) * ROW_HEIGHT}
        width="100%"
      >
        {renderRow}
      </List>
    </div>
  )
}

function VesselTrackingVesselSearch({
  activeImos,
  selectedImos,
  setSelectedImos,
  vesselsData,
  getVesselsLoading,
  segmentFilter,
  dwtMin,
  dwtMax,
  builtMin,
  builtMax,
  designFilter,
  isMultiFilter
}: VesselTrackingVesselSearchProps) {
  const availableVessels = useMemo(() => {
    if (!vesselsData) return []
    const activeImoSet = new Set(activeImos)
    const segmentSet = segmentFilter ? new Set(segmentFilter) : null
    const result: SelectOption[] = []
    for (const vessel of vesselsData) {
      if (activeImoSet.has(String(vessel.value))) continue
      if (segmentSet && !segmentSet.has(vessel.segment)) continue
      if (dwtMin !== null && (vessel.dwt === null || vessel.dwt < dwtMin))
        continue
      if (dwtMax !== null && (vessel.dwt === null || vessel.dwt > dwtMax))
        continue
      if (
        builtMin !== null &&
        (vessel.built === null || vessel.built < builtMin)
      )
        continue
      if (
        builtMax !== null &&
        (vessel.built === null || vessel.built >= builtMax)
      )
        continue
      if (
        designFilter &&
        vessel.design !== designFilter &&
        vessel.designer !== designFilter
      )
        continue
      result.push({
        value: String(vessel.value),
        label: vessel.label.split("(")[0].trim(),
      })
    }
    return result
  }, [
    activeImos,
    vesselsData,
    segmentFilter,
    dwtMin,
    dwtMax,
    builtMin,
    builtMax,
    designFilter,
  ])

  // Auto-select all matching vessels when multi-filter turns on or filters change.
  // Intentionally excludes selectedImos from deps so manual deselects aren't overridden.
  const prevIsMultiFilterRef = useRef(isMultiFilter)
  useEffect(() => {
    if (isMultiFilter) {
      setSelectedImos(availableVessels.map((v) => v.value))
    } else if (prevIsMultiFilterRef.current) {
      setSelectedImos([])
    }
    prevIsMultiFilterRef.current = isMultiFilter
  }, [isMultiFilter, availableVessels, setSelectedImos])

  const selectedImoSet = useMemo(
    () => new Set(selectedImos),
    [selectedImos]
  )

  const value = useMemo(
    () => availableVessels.filter((v) => selectedImoSet.has(v.value)),
    [availableVessels, selectedImoSet]
  )

  const handleSelectAll = () => {
    const merged = new Set(selectedImos)
    for (const v of availableVessels) merged.add(v.value)
    setSelectedImos(Array.from(merged))
  }

  const handleClearAll = () => {
    setSelectedImos([])
  }

  return (
    <Select
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      controlShouldRenderValue={false}
      isClearable={false}
      components={{
        MenuList: VirtualizedMenuList,
        Option: CheckboxOption,
      }}
      styles={{
        control: (base) => ({ ...base, minWidth: 250 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? "#cfe2ff"
            : state.isFocused
            ? "#e9ecef"
            : "white",
          color: "#212529",
          cursor: "pointer",
        }),
      }}
      value={value}
      onChange={(selected) => {
        setSelectedImos(
          (selected as SelectOption[]).map((s) => String(s.value))
        )
      }}
      options={availableVessels}
      isDisabled={getVesselsLoading}
      placeholder={
        getVesselsLoading
          ? "Loading vessels..."
          : selectedImos.length > 0
          ? `${selectedImos.length} selected`
          : "Search Vessel Name / IMO"
      }
      {...({
        onSelectAll: handleSelectAll,
        onClearAll: handleClearAll,
      } as any)}
    />
  )
}

export default VesselTrackingVesselSearch
